import { useState } from "react";
import { Bell, BellOff, BellRing, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tag } from "@/components/common/StatusChip";
import { usePermissionState } from "@/lib/notifications";
import { playChime, setSoundEnabled, soundEnabled, unlockAudio } from "@/lib/sound";

export function NotificationBell() {
  const { state, request, supported } = usePermissionState();
  const [sound, setSound] = useState(soundEnabled());
  if (!supported) return null;
  const enable = async () => {
    const r = await request();
    if (r === "granted") {
      toast.success("Desktop notifications are on — you'll be alerted when a ticket is assigned to you");
      playChime();
      new Notification("Total Tickets", { body: "Desktop notifications are on. You'll hear from us when a ticket lands on you.", tag: "tt-welcome" });
    } else if (r === "denied") toast.error("Notifications are blocked for this site — allow them in your browser's site settings");
  };
  const toggleSound = (on) => { setSoundEnabled(on); setSound(on); unlockAudio(); if (on) playChime({ force: true }); };
  const Icon = state === "granted" ? BellRing : state === "denied" ? BellOff : Bell;
  const label = state === "granted" ? "Desktop notifications on" : state === "denied" ? "Notifications blocked by the browser" : "Enable desktop notifications";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={state === "default" ? "outline" : "ghost"} size={state === "default" ? "sm" : "icon"}
          className={state === "default" ? "border-primary/50 text-primary hover:bg-primary/10 hover:text-primary" : state === "denied" ? "text-red-500" : "text-emerald-500"}
          aria-label={label} title={label} data-testid={`notification-bell-${state}`}>
          <Icon className="h-4 w-4" />{state === "default" && <span className="ml-1.5 hidden text-xs sm:inline">Enable alerts</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 bg-popover p-3" data-testid="notification-popover">
        <div className="text-sm font-bold">Assignment alerts</div>
        <div className="flex items-center justify-between gap-3 text-xs">
          <div>
            <div className="font-semibold">Desktop notification</div>
            <div className="text-[11px] text-muted-foreground">{state === "granted" ? "On — shows even when this tab is hidden." : state === "denied" ? "Blocked. Allow notifications for this site in your browser settings." : "Pops up when a ticket is assigned to you."}</div>
          </div>
          {state === "granted" ? <Tag tone="emerald">on</Tag> : state === "denied" ? <Tag tone="red">blocked</Tag> : <Button size="sm" className="h-7 text-xs" onClick={enable} data-testid="notification-enable-btn">Enable</Button>}
        </div>
        <div className="flex items-center justify-between gap-3 text-xs">
          <div>
            <div className="flex items-center gap-1 font-semibold">{sound ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />} Chime</div>
            <div className="text-[11px] text-muted-foreground">A short three-note chime plays with every alert.</div>
          </div>
          <Switch checked={sound} onCheckedChange={toggleSound} data-testid="notification-sound-switch" />
        </div>
        <Button size="sm" variant="outline" className="h-7 w-full text-xs" onClick={() => { unlockAudio(); playChime({ force: true }) ? toast.info("That's the chime") : toast.error("Sound isn't available in this browser"); }} data-testid="notification-test-sound-btn">Play test chime</Button>
        <p className="text-[11px] text-muted-foreground">In-app toasts always appear while Total Tickets is open; the bell only controls desktop pop-ups and sound.</p>
      </PopoverContent>
    </Popover>
  );
}
